'use client';
import React, { useState } from 'react';
import { Bot, Zap, RefreshCw, Bell, BookOpen, CheckCircle2, Clock, AlertCircle, Brain, Sparkles, Users, ArrowRight, Settings, Eye, Activity } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';


type SyncStatus = 'synced' | 'syncing' | 'pending' | 'failed';

interface TutorAgent {
  id: string;
  name: string;
  personality: string;
  assignedCourses: number;
  activeStudents: number;
  lastSync: string;
  syncStatus: SyncStatus;
  pendingUpdates: number;
  teachingStyle: string;
  memoryEntries: number;
}

interface SyncEvent {
  id: string;
  tutorName: string;
  courseUpdated: string;
  eventType: 'curriculum_update' | 'style_adapt' | 'student_notify' | 'memory_store' | 'version_sync';
  timestamp: string;
  status: 'completed' | 'in_progress' | 'queued';
  detail: string;
}

const tutors: TutorAgent[] = [
  { id: 't-001', name: 'Sunny', personality: 'Playful & Encouraging', assignedCourses: 5, activeStudents: 1240, lastSync: '2 min ago', syncStatus: 'synced', pendingUpdates: 0, teachingStyle: 'Visual storytelling, gentle pacing', memoryEntries: 8420 },
  { id: 't-002', name: 'Nova', personality: 'Curious & Exploratory', assignedCourses: 4, activeStudents: 890, lastSync: '5 min ago', syncStatus: 'syncing', pendingUpdates: 2, teachingStyle: 'Project-based, discovery learning', memoryEntries: 6130 },
  { id: 't-003', name: 'Orion', personality: 'Precise & Challenging', assignedCourses: 5, activeStudents: 340, lastSync: '12 min ago', syncStatus: 'pending', pendingUpdates: 3, teachingStyle: 'Socratic method, proof-based', memoryEntries: 2890 },
  { id: 't-004', name: 'Atlas', personality: 'Expert & Reference-Rich', assignedCourses: 4, activeStudents: 210, lastSync: '1 hour ago', syncStatus: 'failed', pendingUpdates: 5, teachingStyle: 'Case studies, simulations, technical depth', memoryEntries: 1540 },
];

const syncEvents: SyncEvent[] = [
  { id: 'se-001', tutorName: 'Nova', courseUpdated: 'Intro to Coding v4.0.0', eventType: 'curriculum_update', timestamp: '2026-07-10 18:42', status: 'completed', detail: 'Nova absorbed 4 new Python 3.12 modules and updated lesson explanations.' },
  { id: 'se-002', tutorName: 'Nova', courseUpdated: 'Intro to Coding v4.0.0', eventType: 'style_adapt', timestamp: '2026-07-10 18:43', status: 'completed', detail: 'Teaching style adapted: added more interactive code examples per professor notes.' },
  { id: 'se-003', tutorName: 'Nova', courseUpdated: 'Intro to Coding v4.0.0', eventType: 'student_notify', timestamp: '2026-07-10 18:44', status: 'completed', detail: '1,247 enrolled students notified of new content and updated learning paths.' },
  { id: 'se-004', tutorName: 'Orion', courseUpdated: 'Advanced Mathematics v2.1.0', eventType: 'version_sync', timestamp: '2026-07-10 19:01', status: 'in_progress', detail: 'Syncing new calculus modules and proof exercises from Prof. Chen\'s submission.' },
  { id: 'se-005', tutorName: 'Atlas', courseUpdated: 'AI Development v3.2.0', eventType: 'curriculum_update', timestamp: '2026-07-10 19:05', status: 'queued', detail: 'Waiting for admin approval before syncing LLM fine-tuning module.' },
  { id: 'se-006', tutorName: 'Orion', courseUpdated: 'Advanced Mathematics v2.1.0', eventType: 'memory_store', timestamp: '2026-07-10 19:02', status: 'in_progress', detail: 'Storing new learning patterns and student progress checkpoints in memory.' },
];

const statusConfig: Record<SyncStatus, { label: string; color: string; icon: React.ElementType }> = {
  synced: { label: 'Synced', color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle2 },
  syncing: { label: 'Syncing', color: 'text-blue-600 bg-blue-50 border-blue-200', icon: RefreshCw },
  pending: { label: 'Pending', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: Clock },
  failed: { label: 'Failed', color: 'text-red-600 bg-red-50 border-red-200', icon: AlertCircle },
};

const eventTypeConfig: Record<SyncEvent['eventType'], { label: string; color: string; icon: React.ElementType }> = {
  curriculum_update: { label: 'Curriculum Update', color: 'text-blue-600 bg-blue-50', icon: BookOpen },
  style_adapt: { label: 'Style Adaptation', color: 'text-purple-600 bg-purple-50', icon: Brain },
  student_notify: { label: 'Student Notification', color: 'text-emerald-600 bg-emerald-50', icon: Bell },
  memory_store: { label: 'Memory Storage', color: 'text-amber-600 bg-amber-50', icon: Sparkles },
  version_sync: { label: 'Version Sync', color: 'text-rose-600 bg-rose-50', icon: RefreshCw },
};

const eventStatusColor: Record<SyncEvent['status'], string> = {
  completed: 'text-green-600',
  in_progress: 'text-blue-600',
  queued: 'text-muted-foreground',
};

export default function AITutorSyncContent() {
  const [activeTab, setActiveTab] = useState<'tutors' | 'events' | 'settings'>('tutors');
  const [selectedTutor, setSelectedTutor] = useState<TutorAgent | null>(null);
  const [tutorModalOpen, setTutorModalOpen] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);

  const handleSyncAll = async () => {
    setSyncingAll(true);
    await new Promise(r => setTimeout(r, 2000));
    setSyncingAll(false);
  };

  const totalPending = tutors.reduce((a, t) => a + t.pendingUpdates, 0);
  const totalStudents = tutors.reduce((a, t) => a + t.activeStudents, 0);
  const totalMemory = tutors.reduce((a, t) => a + t.memoryEntries, 0);

  const stats = [
    { label: 'Active Tutors', value: tutors.length, icon: Bot, color: 'text-blue-600 bg-blue-50' },
    { label: 'Students Learning', value: totalStudents.toLocaleString(), icon: Users, color: 'text-green-600 bg-green-50' },
    { label: 'Pending Syncs', value: totalPending, icon: Clock, color: 'text-amber-600 bg-amber-50' },
    { label: 'Memory Entries', value: totalMemory.toLocaleString(), icon: Brain, color: 'text-purple-600 bg-purple-50' },
  ];

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground">AI Tutor Curriculum Sync Engine</h1>
          <p className="text-sm text-muted-foreground mt-1">Tutors auto-update when new curriculum is uploaded, adapt teaching style, and notify students</p>
        </div>
        <button
          onClick={handleSyncAll}
          disabled={syncingAll}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors disabled:opacity-70"
        >
          {syncingAll ? (
            <><span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" /> Syncing All...</>
          ) : (
            <><Zap size={16} /> Sync All Tutors</>
          )}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${s.color}`}>
                <Icon size={18} />
              </div>
              <div>
                <p className="text-xl font-extrabold text-foreground font-tabular">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {(['tutors', 'events', 'settings'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-semibold capitalize transition-all border-b-2 -mb-px ${
              activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'tutors' ? 'Tutor Agents' : tab === 'events' ? 'Sync Activity' : 'Sync Settings'}
          </button>
        ))}
      </div>

      {/* Tutors Tab */}
      {activeTab === 'tutors' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tutors.map((tutor) => {
            const StatusIcon = statusConfig[tutor.syncStatus].icon;
            return (
              <div key={tutor.id} className="bg-card border border-border rounded-2xl p-5 card-hover">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 relative">
                    <Bot size={22} className="text-primary" />
                    {tutor.syncStatus === 'syncing' && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-card animate-pulse" />
                    )}
                    {tutor.pendingUpdates > 0 && tutor.syncStatus !== 'syncing' && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full border-2 border-card flex items-center justify-center">
                        <span className="text-white text-[8px] font-bold">{tutor.pendingUpdates}</span>
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-bold text-foreground">{tutor.name}</h3>
                      <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${statusConfig[tutor.syncStatus].color}`}>
                        <StatusIcon size={9} className={tutor.syncStatus === 'syncing' ? 'animate-spin' : ''} />
                        {statusConfig[tutor.syncStatus].label}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{tutor.personality}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 italic">"{tutor.teachingStyle}"</p>
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {[
                        { label: 'Courses', value: tutor.assignedCourses },
                        { label: 'Students', value: tutor.activeStudents.toLocaleString() },
                        { label: 'Memories', value: tutor.memoryEntries.toLocaleString() },
                      ].map((m) => (
                        <div key={m.label} className="bg-muted/50 rounded-lg p-2 text-center">
                          <p className="text-sm font-bold text-foreground font-tabular">{m.value}</p>
                          <p className="text-xs text-muted-foreground">{m.label}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                      <span className="text-xs text-muted-foreground">Last sync: {tutor.lastSync}</span>
                      <button
                        onClick={() => { setSelectedTutor(tutor); setTutorModalOpen(true); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-semibold hover:bg-primary/20 transition-colors"
                      >
                        <Eye size={11} /> View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sync Events Tab */}
      {activeTab === 'events' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Activity size={13} />
            <span>Live sync activity log — updates every 30 seconds</span>
            <span className="w-2 h-2 bg-green-500 rounded-full pulse-dot" />
          </div>
          {syncEvents.map((event) => {
            const EventIcon = eventTypeConfig[event.eventType].icon;
            return (
              <div key={event.id} className="bg-card border border-border rounded-2xl p-4 flex items-start gap-4">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${eventTypeConfig[event.eventType].color}`}>
                  <EventIcon size={16} className={event.status === 'in_progress' ? 'animate-spin' : ''} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs font-bold text-foreground">{event.tutorName}</span>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${eventTypeConfig[event.eventType].color}`}>
                      {eventTypeConfig[event.eventType].label}
                    </span>
                    <span className={`text-xs font-semibold capitalize ${eventStatusColor[event.status]}`}>
                      {event.status.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-foreground">{event.courseUpdated}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{event.detail}</p>
                </div>
                <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">{event.timestamp.split(' ')[1]}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="max-w-2xl space-y-4">
          <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
            <h2 className="text-base font-bold text-foreground">Sync Engine Configuration</h2>
            {[
              { label: 'Auto-sync on curriculum approval', description: 'Tutors automatically update when a new curriculum version is approved', enabled: true },
              { label: 'Notify students on content update', description: 'Send in-app notifications to enrolled students when their course is updated', enabled: true },
              { label: 'Teaching style auto-adaptation', description: 'Tutors adjust their teaching approach based on professor-authored notes', enabled: true },
              { label: 'Learning memory migration', description: 'Migrate student learning memories when a course version changes', enabled: false },
              { label: 'Require admin approval before sync', description: 'Hold tutor sync until an admin manually approves the curriculum version', enabled: false },
            ].map((setting) => (
              <div key={setting.label} className="flex items-start justify-between gap-4 py-3 border-b border-border last:border-0">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{setting.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{setting.description}</p>
                </div>
                <div className={`w-10 h-6 rounded-full flex items-center shrink-0 cursor-pointer transition-colors ${setting.enabled ? 'bg-primary justify-end' : 'bg-muted justify-start'}`}>
                  <div className="w-4 h-4 bg-white rounded-full mx-1 shadow-sm" />
                </div>
              </div>
            ))}
          </div>

          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-3">Sync Pipeline Flow</h3>
            <div className="space-y-2">
              {[
                { step: '1', label: 'Curriculum Approved', desc: 'Admin approves new version in review queue', icon: CheckCircle2, color: 'text-green-600 bg-green-50' },
                { step: '2', label: 'Tutor Ingestion', desc: 'AI tutor reads new modules and professor notes', icon: Brain, color: 'text-blue-600 bg-blue-50' },
                { step: '3', label: 'Style Adaptation', desc: 'Tutor adjusts teaching approach for new content', icon: Sparkles, color: 'text-purple-600 bg-purple-50' },
                { step: '4', label: 'Memory Update', desc: 'New learning patterns stored in tutor memory', icon: Bot, color: 'text-amber-600 bg-amber-50' },
                { step: '5', label: 'Student Notification', desc: 'Enrolled students notified of new content', icon: Bell, color: 'text-rose-600 bg-rose-50' },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={s.step} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${s.color}`}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-bold text-foreground">{s.label}</p>
                      <p className="text-xs text-muted-foreground">{s.desc}</p>
                    </div>
                    {i < 4 && <ArrowRight size={12} className="text-muted-foreground shrink-0 rotate-90 sm:rotate-0" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Tutor Detail Modal */}
      <Modal open={tutorModalOpen} onClose={() => setTutorModalOpen(false)} title="Tutor Agent Details" size="md">
        {selectedTutor && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Bot size={26} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground">{selectedTutor.name}</h3>
                <p className="text-sm text-muted-foreground">{selectedTutor.personality}</p>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border mt-1 ${statusConfig[selectedTutor.syncStatus].color}`}>
                  {statusConfig[selectedTutor.syncStatus].label}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Assigned Courses', value: selectedTutor.assignedCourses },
                { label: 'Active Students', value: selectedTutor.activeStudents.toLocaleString() },
                { label: 'Memory Entries', value: selectedTutor.memoryEntries.toLocaleString() },
                { label: 'Pending Updates', value: selectedTutor.pendingUpdates },
              ].map((m) => (
                <div key={m.label} className="bg-muted rounded-xl p-3">
                  <p className="text-lg font-bold text-foreground font-tabular">{m.value}</p>
                  <p className="text-xs text-muted-foreground">{m.label}</p>
                </div>
              ))}
            </div>
            <div className="p-3 bg-muted/50 rounded-xl">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Teaching Style</p>
              <p className="text-sm text-foreground">{selectedTutor.teachingStyle}</p>
            </div>
            {selectedTutor.pendingUpdates > 0 && (
              <button className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors">
                <Zap size={14} className="inline mr-1.5" /> Sync {selectedTutor.pendingUpdates} Pending Update{selectedTutor.pendingUpdates > 1 ? 's' : ''}
              </button>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
